import {
  TransferReviewSection,
  VxlListItemTrailingAccessoryResultTypenames,
} from '@varo/graphql-schema-types';
import type { ReactElement } from 'react';
import { SectionList, StyleSheet, View } from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

function TransferReviewSections({
  sections,
  header,
  accessibilityLabel,
}: {
  sections: TransferReviewSection[];
  header: ReactElement;
  accessibilityLabel: string;
}) {
  const lastSectionId = sections.at(-1)?.id;

  return (
    <SectionList
      accessibilityLabel={accessibilityLabel}
      style={styles.list}
      contentContainerStyle={styles.content}
      ListHeaderComponent={header}
      stickySectionHeadersEnabled={false}
      sections={sections.map((section) => ({
        ...section,
        key: section.id,
        data: section.items,
      }))}
      renderSectionHeader={({ section }) =>
        section.title ? (
          <TitleSmText
            numberOfLines={0}
            accessibilityRole="header"
            style={styles.sectionTitle}
          >
            {section.title}
          </TitleSmText>
        ) : null
      }
      renderItem={({ item, index, section }) => {
        const trailing =
          item.trailingAccessory?.__typename ===
          VxlListItemTrailingAccessoryResultTypenames.VxlListItemTrailingTextAccessory
            ? item.trailingAccessory
            : undefined;

        return (
          <AmplitudeMask>
            <ListItem
              accessibilityLabel={item.accessibilityLabel ?? undefined}
              primaryCopy={item.primaryText}
              secondaryCopy={item.secondaryText}
              primaryNumberOfLines={item.primaryNumberOfLines ?? 0}
              secondaryNumberOfLines={0}
              boldPrimaryCopy={item.boldPrimaryCopy ?? false}
              isDisabled={item.isDisabled}
              showsChevron={item.showsChevron}
              colorTheme="transparent"
              trailingAccessory={
                trailing
                  ? {
                      id: `${section.id}-${index}-trailing`,
                      name: VXLComponentId.LIST_ITEM_TEXT_STACK,
                      props: {
                        primaryCopy: trailing.primaryCopy,
                        secondaryCopy: trailing.secondaryCopy,
                        primaryNumberOfLines:
                          trailing.primaryNumberOfLines ?? 0,
                        secondaryNumberOfLines: 0,
                        boldPrimaryCopy: trailing.boldPrimaryCopy,
                      },
                    }
                  : undefined
              }
            />
          </AmplitudeMask>
        );
      }}
      renderSectionFooter={({ section }) =>
        section.id !== lastSectionId ? (
          <View style={styles.divider}>
            <HairLineDivider />
          </View>
        ) : null
      }
    />
  );
}

const styles = StyleSheet.create({
  list: { flex: 1 },
  content: { paddingTop: theme.spacing.m },
  sectionTitle: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  divider: {
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.s,
  },
});

export default TransferReviewSections;
